import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Camera,
  X,
  Lock,
  PenLine,
  Image as ImageIcon,
  Bell,
  BellOff,
} from "@/components/icons";
import { toast } from "sonner";
import { MEDICAL_SPECIALTIES } from "../lib/specialties";
import Portal from "./Portal";
import AddressAutocomplete from "./AddressAutocomplete";
import { getPref, setPref, PREF_KEYS } from "../lib/userPrefs";

const ROLE_LABEL = {
  doctor: "Doctor",
  secretary: "Secretaria",
  admin: "Admin",
  pharmacy: "Farmacia",
};

/**
 * Modal "Mi perfil" en estilo Lunio. Replica el diseño solicitado:
 * encabezado "Mi perfil" + subtítulo, panel gris con tabs blancos
 * (Perfil / Marca / Contraseña) y formularios en tarjetas blancas
 * redondeadas. Se monta solo cuando se abre desde el avatar del header,
 * por eso usamos useState con valores iniciales del `user` actual.
 */
export default function ProfileDialog({ onClose, initialTab = "profile", embedded = false }) {
  const { user, setUser } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [specialty, setSpecialty] = useState(user?.specialty || "");
  const [photo, setPhoto] = useState(user?.photo_url || null);
  const [signature, setSignature] = useState(user?.signature_url || null);
  const [clinicLogo, setClinicLogo] = useState(user?.clinic_logo_url || null);
  const [saving, setSaving] = useState(false);
  const [curPass, setCurPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const fileRef = useRef();
  const sigRef = useRef();
  const clinicRef = useRef();
  const stampRef = useRef();
  const [stamp, setStamp] = useState(user?.stamp_url || null);
  const [clinicName, setClinicName] = useState(user?.clinic_name || "");
  const [clinicPhone, setClinicPhone] = useState(user?.clinic_phone || "");
  const [exequatur, setExequatur] = useState(user?.exequatur_number || "");
  const [cedula, setCedula] = useState(user?.cedula || "");

  const isDoctor = user?.role === "doctor";

  const onPickImage = (e, setter, maxKb = 5000) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > maxKb * 1000) {
      toast.error(
        `La imagen es muy grande (máx ${
          maxKb >= 1000 ? `${maxKb / 1000}MB` : `${maxKb}KB`
        })`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setter(reader.result);
    reader.readAsDataURL(f);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", {
        name,
        phone,
        address,
        photo_url: photo,
        ...(isDoctor ? { specialty: specialty || null } : {}),
      });
      setUser(data);
      toast.success("Perfil actualizado");
      onClose();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveBrand = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", {
        signature_url: signature,
        stamp_url: stamp,
        clinic_logo_url: clinicLogo,
        clinic_name: clinicName || null,
        clinic_phone: clinicPhone || null,
        exequatur_number: exequatur || null,
        cedula: cedula || null,
      });
      setUser(data);
      toast.success("Marca y datos del certificado actualizados");
      onClose();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPass !== newPass2) {
      toast.error("Las contraseñas nuevas no coinciden");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/me/password", {
        current_password: curPass,
        new_password: newPass,
      });
      toast.success("Contraseña actualizada");
      onClose();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileShell embedded={embedded} onOverlayClick={onClose}>
      <div
        className={embedded ? "w-full" : "w-full max-w-2xl my-6"}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Card */}
        <div className="bg-white rounded-[28px] border border-black/[0.06] p-6 sm:p-8 relative">
          {/* Header */}
          <div className="flex items-start justify-between mb-6 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-extrabold text-[#01BC8E] mb-1">
                Cuenta
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-black leading-none">
                Mi perfil
              </h1>
              <p className="text-sm text-black/55 mt-1.5">
                Actualiza tu información, marca y contraseña.
              </p>
            </div>
            {!embedded && (
              <button
                type="button"
                onClick={onClose}
                data-testid="profile-dialog-close"
              className="w-9 h-9 rounded-xl bg-[#F5F5F5] text-black/70 hover:bg-black/[0.08] hover:text-black flex items-center justify-center flex-shrink-0 transition"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

            {/* Tabs — segmented control con fondo gris */}
            <div className="bg-[#F5F5F5] rounded-full p-1 grid grid-cols-4 gap-1 mb-6">
              <TabBtn active={tab === "profile"} onClick={() => setTab("profile")} testid="tab-profile">
                Perfil
              </TabBtn>
              <TabBtn active={tab === "brand"} onClick={() => setTab("brand")} testid="tab-brand">
                Marca
              </TabBtn>
              <TabBtn active={tab === "preferences"} onClick={() => setTab("preferences")} testid="tab-preferences">
                Preferencias
              </TabBtn>
              <TabBtn active={tab === "password"} onClick={() => setTab("password")} testid="tab-password">
                Contraseña
              </TabBtn>
            </div>

            {tab === "profile" && (
              <form onSubmit={saveProfile} className="space-y-4">
                {/* Avatar block — estilo Apple ID: centrado, grande */}
                <div className="flex flex-col items-center gap-3 pb-2">
                  <div className="relative">
                    {photo ? (
                      <img
                        src={photo}
                        alt="avatar"
                        className="w-28 h-28 rounded-full object-cover bg-white ring-1 ring-black/[0.05]"
                      />
                    ) : (
                      <div className="w-28 h-28 rounded-full bg-[#F2F3F5] flex items-center justify-center text-4xl font-semibold text-black/70 ring-1 ring-black/[0.05]">
                        {user?.name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="photo-upload-button"
                      className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#0071E3] hover:bg-[#0077ED] text-white flex items-center justify-center shadow-md transition"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onPickImage(e, setPhoto, 800)}
                      data-testid="photo-file-input"
                    />
                  </div>
                  <div className="text-center">
                    <div className="text-[16px] font-semibold text-black">
                      {user?.name || user?.full_name}
                    </div>
                    <div className="text-[12.5px] text-black/50">{user?.email}</div>
                    <div className="text-[11.5px] text-[#0071E3] font-medium mt-0.5">
                      {ROLE_LABEL[user?.role] || user?.role}
                    </div>
                  </div>
                </div>

                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre"
                  testid="profile-name"
                  required
                />
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Teléfono"
                />
                <AddressAutocomplete
                  value={address}
                  onChange={(v) => setAddress(v)}
                  placeholder="Dirección"
                  showIcon={false}
                  inputClassName="w-full bg-white rounded-2xl px-4 py-3 text-sm text-black placeholder-black/40 focus:outline-none border border-transparent focus:border-black/15 transition"
                  testid="profile-address"
                />
                {isDoctor && (
                  <Select
                    value={specialty}
                    onChange={(e) => setSpecialty(e.target.value)}
                    options={MEDICAL_SPECIALTIES}
                    placeholder="Selecciona tu especialidad"
                    testid="profile-specialty"
                  />
                )}

                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    disabled={saving}
                    data-testid="profile-save"
                    className="btn-primary disabled:opacity-60"
                  >
                    {saving ? "Guardando..." : "Guardar"}
                  </button>
                </div>
              </form>
            )}

            {tab === "brand" && (
              <form onSubmit={saveBrand} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <BrandSlot
                    label="Firma digital"
                    icon={PenLine}
                    value={signature}
                    onPick={() => sigRef.current?.click()}
                    onClear={() => setSignature(null)}
                  />
                  <input
                    ref={sigRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPickImage(e, setSignature, 1000)}
                  />
                  <BrandSlot
                    label="Sello profesional"
                    icon={ImageIcon}
                    value={stamp}
                    onPick={() => stampRef.current?.click()}
                    onClear={() => setStamp(null)}
                  />
                  <input
                    ref={stampRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPickImage(e, setStamp, 1000)}
                  />
                  <BrandSlot
                    label="Logo de la clínica"
                    icon={ImageIcon}
                    value={clinicLogo}
                    onPick={() => clinicRef.current?.click()}
                    onClear={() => setClinicLogo(null)}
                  />
                  <input
                    ref={clinicRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onPickImage(e, setClinicLogo, 1500)}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <CertField
                    label="Nombre de la clínica / centro"
                    value={clinicName}
                    onChange={setClinicName}
                    placeholder="Ej. Instituto Materno Infantil"
                    testid="cert-clinic-name"
                  />
                  <CertField
                    label="Teléfono de la oficina"
                    value={clinicPhone}
                    onChange={setClinicPhone}
                    placeholder="Ej. 809-000-0000"
                    testid="cert-clinic-phone"
                  />
                  <CertField
                    label="No. Exequátur"
                    value={exequatur}
                    onChange={setExequatur}
                    placeholder="Ej. 391-16"
                    testid="cert-exequatur"
                  />
                  <CertField
                    label="Cédula"
                    value={cedula}
                    onChange={setCedula}
                    placeholder="Ej. 031-0000000-0"
                    testid="cert-cedula"
                  />
                </div>
                <p className="text-xs text-black/50">
                  Estos elementos aparecen en tus recetas y certificados médicos.
                </p>
                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    disabled={saving}
                    data-testid="brand-save"
                    className="btn-primary disabled:opacity-60"
                  >
                    {saving ? "Guardando..." : "Guardar"}
                  </button>
                </div>
              </form>
            )}

            {tab === "preferences" && <PreferencesPanel />}

            {tab === "password" && (
              <form onSubmit={savePassword} className="space-y-3">
                <div className="flex items-center gap-2 mb-1 text-black/75 font-bold text-sm">
                  <Lock className="w-4 h-4" /> Cambiar contraseña
                </div>
                <Input
                  type="password"
                  required
                  value={curPass}
                  onChange={(e) => setCurPass(e.target.value)}
                  placeholder="Contraseña actual"
                  testid="pass-current"
                />
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                  placeholder="Nueva contraseña (mín. 6 caracteres)"
                  testid="pass-new"
                />
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={newPass2}
                  onChange={(e) => setNewPass2(e.target.value)}
                  placeholder="Confirmar nueva contraseña"
                  testid="pass-confirm"
                />
                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    disabled={saving}
                    data-testid="pass-save"
                    className="btn-primary disabled:opacity-60"
                  >
                    {saving ? "Guardando..." : "Cambiar contraseña"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
    </ProfileShell>
  );
}

/**
 * Wrapper que decide si el ProfileDialog se renderiza como modal
 * (Portal + overlay negro) o embebido en una página (sin overlay).
 */
function ProfileShell({ embedded, onOverlayClick, children }) {
  if (embedded) return <>{children}</>;
  return (
    <Portal>
      <div
        className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/30 backdrop-blur-sm p-4 overflow-y-auto"
        onClick={onOverlayClick}
        data-testid="profile-dialog-overlay"
      >
        {children}
      </div>
    </Portal>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative py-2 rounded-xl text-sm font-bold transition ${
        active
          ? "bg-white text-black shadow-[0_2px_8px_-2px_rgba(0,0,0,0.10)]"
          : "text-black/55 hover:text-black/85"
      }`}
    >
      {children}
    </button>
  );
}

function Input({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-white rounded-2xl px-4 py-3 text-sm text-black placeholder-black/40 focus:outline-none border border-transparent focus:border-black/15 transition"
    />
  );
}

function Select({ value, onChange, options, placeholder, testid }) {
  return (
    <div className="relative">
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className={`w-full appearance-none bg-white rounded-2xl px-4 pr-10 py-3 text-sm focus:outline-none border border-transparent focus:border-black/15 transition ${
          value ? "text-black" : "text-black/40"
        }`}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt} className="text-black">
            {opt}
          </option>
        ))}
      </select>
      <svg
        className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-black/45 pointer-events-none"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
}

function BrandSlot({ label, icon: Icon, value, onPick, onClear }) {
  return (
    <div className="bg-[#F5F5F5] rounded-2xl border border-black/[0.05] p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-extrabold text-black uppercase tracking-wide">
          <Icon className="w-3.5 h-3.5 text-black/60" />
          {label}
        </div>
        {value && (
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] font-bold text-black/50 hover:text-red-500 uppercase tracking-wider"
          >
            Quitar
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onPick}
        className={`w-full h-24 rounded-xl flex items-center justify-center text-xs font-bold transition overflow-hidden ${
          value
            ? "bg-white border border-black/[0.06] hover:border-[#01BC8E]/40"
            : "bg-white border-2 border-dashed border-black/15 text-black/50 hover:border-[#01BC8E] hover:text-[#01BC8E] hover:bg-[#01BC8E]/5"
        }`}
      >
        {value ? (
          <img src={value} alt={label} className="max-h-full max-w-full object-contain p-2" />
        ) : (
          <span className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            Subir imagen
          </span>
        )}
      </button>
    </div>
  );
}

function CertField({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-extrabold text-black/60">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1.5 w-full bg-[#F5F5F5] rounded-xl px-4 py-2.5 text-sm border border-transparent focus:bg-white focus:border-[#01BC8E]/40 focus:outline-none transition placeholder:text-black/35"
      />
    </label>
  );
}



// ─── Preferencias (toggles de notificaciones) ────────────────────────
function PreferencesPanel() {
  const [chatSound, setChatSound] = useState(() =>
    getPref(PREF_KEYS.CHAT_SOUND, true)
  );

  // Playback de preview al activar
  const previewSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      [{ f: 880, t: 0, d: 0.12 }, { f: 1318, t: 0.09, d: 0.18 }].forEach(
        ({ f, t, d }) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine";
          o.frequency.setValueAtTime(f, now + t);
          g.gain.setValueAtTime(0, now + t);
          g.gain.linearRampToValueAtTime(0.12, now + t + 0.01);
          g.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
          o.connect(g);
          g.connect(ctx.destination);
          o.start(now + t);
          o.stop(now + t + d + 0.02);
        }
      );
      setTimeout(() => ctx.close().catch(() => {}), 500);
    } catch {
      /* ignore */
    }
  };

  const toggleChatSound = () => {
    const next = !chatSound;
    setChatSound(next);
    setPref(PREF_KEYS.CHAT_SOUND, next);
    if (next) previewSound();
  };

  return (
    <div className="space-y-3" data-testid="preferences-panel">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-black/45 mb-1">
        Notificaciones
      </div>
      <PreferenceRow
        icon={chatSound ? Bell : BellOff}
        title="Sonido al conectar un contacto"
        subtitle="Reproduce un pequeño ding cuando un contacto se conecta en el chat"
        active={chatSound}
        onToggle={toggleChatSound}
        testid="pref-chat-sound"
      />
      <PushPrefsSection />
    </div>
  );
}

const PUSH_ITEMS = [
  ["referrals", "Referencias nuevas", "Cuando otro doctor te refiere un paciente o responde una referencia"],
  ["messages", "Mensajes de chat", "Cuando recibes un mensaje nuevo"],
  ["appointments", "Citas nuevas y cambios", "Citas agendadas, confirmadas, canceladas o reprogramadas"],
  ["reminders", "Recordatorio 30 min antes", "Aviso antes de que empiece cada cita"],
];

function PushPrefsSection() {
  const [prefs, setPrefs] = useState(null);
  const [devices, setDevices] = useState([]);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/push/preferences").then((r) => setPrefs(r.data)).catch(() => {});
    api.get("/push/devices").then((r) => setDevices(r.data)).catch(() => {});
  }, []);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await api.put("/push/preferences", { [key]: next[key] });
    } catch (e) {
      setPrefs(prefs);
      toast.error(formatErr(e));
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/push/test");
      if (data.sent) toast.success("Notificación de prueba enviada a tu teléfono");
      else toast.error("No se pudo entregar. Abre la app móvil para reconectar tu teléfono.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setTesting(false);
    }
  };

  if (!prefs) return null;
  return (
    <div className="pt-4 space-y-3" data-testid="push-prefs-section">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-black/45">
          Notificaciones push · App móvil
        </div>
        <span className="text-[11px] text-black/50" data-testid="push-devices-count">
          {devices.length ? `${devices.length} dispositivo(s) conectado(s)` : "Sin dispositivos conectados"}
        </span>
      </div>
      {PUSH_ITEMS.map(([key, title, subtitle]) => (
        <PreferenceRow
          key={key}
          icon={prefs[key] ? Bell : BellOff}
          title={title}
          subtitle={subtitle}
          active={!!prefs[key]}
          onToggle={() => toggle(key)}
          testid={`push-pref-${key}`}
        />
      ))}
      <button
        type="button"
        onClick={sendTest}
        disabled={testing || !devices.length}
        data-testid="push-test-btn"
        className="w-full h-11 rounded-full bg-[#F2F3F5] text-[13px] font-semibold text-black hover:bg-black/[0.08] transition-colors disabled:opacity-40"
      >
        {testing ? "Enviando…" : "Enviar notificación de prueba"}
      </button>
      {!devices.length && (
        <p className="text-[12px] text-black/50 text-center" data-testid="push-no-devices-hint">
          Inicia sesión en la app móvil de ConsuDoctor para recibir notificaciones en tu teléfono.
        </p>
      )}
    </div>
  );
}

function PreferenceRow({ icon: Icon, title, subtitle, active, onToggle, testid }) {
  return (
    <div
      className="flex items-center justify-between gap-4 bg-white rounded-2xl border border-black/[0.05] p-4"
      data-testid={testid}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            active ? "bg-[#0071E3]/10 text-[#0071E3]" : "bg-[#F2F3F5] text-black/50"
          }`}
        >
          <Icon className="w-5 h-5" strokeWidth={1.75} weight="regular" />
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-black">{title}</div>
          <div className="text-[12px] text-black/55 mt-0.5">{subtitle}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${testid}-toggle`}
        role="switch"
        aria-checked={active}
        className={`relative w-11 h-6 rounded-full transition flex-shrink-0 ${
          active ? "bg-[#22C55E]" : "bg-black/15"
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-all ${
            active ? "left-5" : "left-0.5"
          }`}
        />
      </button>
    </div>
  );
}
